import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { FileSystem, Prompt, AppSettings, ItemKind } from '../../types';
import { KIND_META } from '../../utils/itemKinds';
import { useHistory } from '../hooks/useHistory';

export type SortBy = 'date-new' | 'date-old' | 'name-asc' | 'name-desc' | 'usage' | 'favorites';
export type KindFilter = ItemKind | 'all';
import { 
  updatePromptInFS, 
  deletePromptFromFS, 
  findPromptAndParentFolder,
  findFolder
} from '../../utils/fileSystemUtils';

export interface PromptState {
  fileSystem: FileSystem;
  setFileSystem: React.Dispatch<React.SetStateAction<FileSystem>>;
  templates: Prompt[];
  setTemplates: React.Dispatch<React.SetStateAction<Prompt[]>>;
  selectedFolderId: string | null;
  setSelectedFolderId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedPromptId: string | null;
  setSelectedPromptId: React.Dispatch<React.SetStateAction<string | null>>;
  activeTag: string | null;
  setActiveTag: React.Dispatch<React.SetStateAction<string | null>>;
  selectedSpecialView: string | null;
  setSelectedSpecialView: React.Dispatch<React.SetStateAction<string | null>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  settings?: AppSettings;
  setSettings?: (settings: AppSettings | ((prev: AppSettings) => AppSettings)) => void;
  showToast: (message: string, type?: 'success' | 'error', onUndo?: () => void) => void;
  forceImmediateSync?: (fs?: FileSystem, templates?: Prompt[]) => Promise<boolean>;
  modalState: any;
  setModalState: (state: any) => void;
  contextMenu: any;
  setContextMenu: (state: any) => void;
  closeSidebar?: () => void;
  kindFilter: KindFilter;
  setKindFilter: (kind: KindFilter) => void;
  sortBy: SortBy;
  setSortBy: (sort: SortBy) => void;
  /** Gemeinsamer Rückgängig-Verlauf für alle Komponenten */
  pushHistory: () => void;
  undo: () => { fileSystem: FileSystem; templates: Prompt[] } | null;
  canUndo: boolean;
}

/** Was App.tsx hereinreicht; Modal-, Menü-, Sortier- und Verlaufszustand verwaltet der Provider selbst. */
export type PromptInit = Omit<
  PromptState,
  'modalState' | 'setModalState' | 'contextMenu' | 'setContextMenu' | 'sortBy' | 'setSortBy' | 'pushHistory' | 'undo' | 'canUndo' | 'kindFilter' | 'setKindFilter'
> & Partial<Pick<PromptState, 'kindFilter' | 'setKindFilter'>>;

export interface PromptActions {
  handleDeletePrompt: (id: string) => void;
  handleToggleFavorite: (id: string) => void;
  handleUpdatePrompt: (updatedPrompt: Prompt) => void;
  createNewPrompt: (folderId: string | null, fromTemplate?: Partial<Prompt>) => void;
  clearSelection: () => void;
}

export interface PromptContextValue extends PromptState, PromptActions {}

const PromptContext = createContext<PromptContextValue | undefined>(undefined);

export const PromptProvider: React.FC<{ children: ReactNode; initialState: PromptInit }> = ({ children, initialState }) => {
  const {
    fileSystem = [],
    setFileSystem = () => {},
    templates = [],
    setTemplates = () => {},
    selectedFolderId = null,
    setSelectedFolderId = () => {},
    selectedPromptId = null,
    setSelectedPromptId = () => {},
    activeTag = null,
    setActiveTag = () => {},
    selectedSpecialView = null,
    setSelectedSpecialView = () => {},
    searchTerm = '',
    setSearchTerm = () => {},
    settings = { globalSystemInstruction: '', promptGeneratorSystemPrompt: '', promptOptimizerSystemPrompt: '' },
    setSettings = () => {},
    showToast = () => {},
    forceImmediateSync = async () => true,
    closeSidebar = () => {},
  } = initialState || ({} as PromptInit);

  const [modalState, setModalState] = useState<any>(null);
  const [contextMenu, setContextMenu] = useState<any>(null);
  const [sortBy, setSortBy] = useState<SortBy>('date-new');
  const [localKindFilter, setLocalKindFilter] = useState<KindFilter>('all');
  const kindFilter = initialState?.kindFilter ?? localKindFilter;
  const setKindFilter = initialState?.setKindFilter ?? setLocalKindFilter;

  const history = useHistory<{ fileSystem: FileSystem; templates: Prompt[] }>();
  const pushHistory = useCallback(() => {
    history.pushState({ fileSystem, templates });
  }, [history, fileSystem, templates]);
  const undo = useCallback(() => history.undo({ fileSystem, templates }), [history, fileSystem, templates]);

  const clearSelection = useCallback(() => {
    setSelectedFolderId(null);
    setSelectedPromptId(null);
    setActiveTag(null);
    setSelectedSpecialView(null);
    setSearchTerm('');
  }, [setSelectedFolderId, setSelectedPromptId, setActiveTag, setSelectedSpecialView, setSearchTerm]);

  const handleUpdatePrompt = useCallback((updatedPrompt: Prompt) => {
    pushHistory();
    setFileSystem(current => {
      const next = updatePromptInFS(current, updatedPrompt);
      forceImmediateSync?.(next, templates);
      return next;
    });
  }, [pushHistory, setFileSystem, forceImmediateSync, templates]);

  const handleDeletePrompt = useCallback((id: string) => {
    setFileSystem((current) => {
      const next = deletePromptFromFS(current, id);
      forceImmediateSync?.(next, templates);
      return next;
    });
    if (selectedPromptId === id) setSelectedPromptId(null);
    showToast('Prompt in den Papierkorb verschoben', 'success');
  }, [setFileSystem, forceImmediateSync, templates, selectedPromptId, setSelectedPromptId, showToast]);

  const handleToggleFavorite = useCallback((id: string) => {
    setFileSystem((current) => {
      const info = findPromptAndParentFolder(current, id);
      if (info) {
        const updated = { ...info.prompt, isFavorite: !info.prompt.isFavorite };
        const next = updatePromptInFS(current, updated);
        forceImmediateSync?.(next, templates);
        return next;
      }
      return current;
    });
  }, [setFileSystem, forceImmediateSync, templates]);

  const createNewPrompt = useCallback((folderId: string | null, fromTemplate?: Partial<Prompt>) => {
    const kind = fromTemplate?.kind ?? 'prompt';
    const newPrompt: Prompt = {
      ...(fromTemplate || {}),
      id: uuidv4(),
      kind,
      name: fromTemplate?.name || KIND_META[kind].defaultName,
      content: fromTemplate?.content || '',
      tags: fromTemplate?.tags || [],
      isFavorite: fromTemplate?.isFavorite || false,
      updatedAt: new Date().toISOString(),
      versions: fromTemplate?.versions || [],
      useCount: 0,
      lastUsed: null,
      deletedAt: null,
    };

    const targetFolderId = folderId ?? selectedFolderId ?? null;

    setFileSystem(currentFS => {
      const newFS: FileSystem = structuredClone(currentFS);
      let targetFolder = targetFolderId ? findFolder(newFS, targetFolderId) : newFS[0];
      
      if (!targetFolder) {
        if (newFS.length === 0) {
          const newRootFolder = { id: uuidv4(), name: 'Mein erster Ordner', prompts: [], subFolders: [] };
          newFS.push(newRootFolder);
          targetFolder = newRootFolder;
        } else {
          targetFolder = newFS[0];
        }
      }
      targetFolder.prompts.unshift(newPrompt);
      forceImmediateSync?.(newFS, templates);
      return newFS;
    });

    setSelectedPromptId(newPrompt.id);
    setSelectedSpecialView(null);
  }, [selectedFolderId, setFileSystem, setSelectedPromptId, setSelectedSpecialView, forceImmediateSync, templates]);

  const value: PromptContextValue = useMemo(
    () => ({
      fileSystem,
      setFileSystem,
      templates,
      setTemplates,
      selectedFolderId,
      setSelectedFolderId,
      selectedPromptId,
      setSelectedPromptId,
      activeTag,
      setActiveTag,
      selectedSpecialView,
      setSelectedSpecialView,
      searchTerm,
      setSearchTerm,
      settings,
      setSettings,
      handleDeletePrompt,
      handleToggleFavorite,
      handleUpdatePrompt,
      createNewPrompt,
      clearSelection,
      showToast,
      forceImmediateSync,
      modalState,
      setModalState,
      contextMenu,
      setContextMenu,
      closeSidebar,
      kindFilter,
      setKindFilter,
      sortBy,
      setSortBy,
      pushHistory,
      undo,
      canUndo: history.canUndo,
    }),
    [
      fileSystem,
      setFileSystem,
      templates,
      setTemplates,
      selectedFolderId,
      setSelectedFolderId,
      selectedPromptId,
      setSelectedPromptId,
      activeTag,
      setActiveTag,
      selectedSpecialView,
      setSelectedSpecialView,
      searchTerm,
      setSearchTerm,
      settings,
      setSettings,
      handleDeletePrompt,
      handleToggleFavorite,
      handleUpdatePrompt,
      createNewPrompt,
      clearSelection,
      showToast,
      forceImmediateSync,
      modalState,
      contextMenu,
      closeSidebar,
      kindFilter,
      setKindFilter,
      sortBy,
      pushHistory,
      undo,
      history.canUndo,
    ]
  );

  return <PromptContext.Provider value={value}>{children}</PromptContext.Provider>;
};

export const usePromptContext = (): PromptContextValue => {
  const ctx = useContext(PromptContext);
  if (!ctx) {
    throw new Error('usePromptContext must be used within PromptProvider');
  }
  return ctx;
};
